import React, { useMemo } from 'react'
import { Alert } from 'react-bootstrap'

import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { triggerCrossClientActionsPolling } from '../../../../store/crossClient/crossClientActions'
import { useUpdateCrossClientIntegrationEntitiesMutation } from '../../../../store/crossClient/crossClient'
import { useGetConnectorEntitiesQuery, type WealthboxTag } from '../../../../store/integrations/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { IntegrationServiceId } from '../../../../common/integrations'
import { Spinner } from '../../../../components/Spinner/Spinner'

import { TagsModal, type TagOption } from '../TagsModal'
import { type CrmActionModalProps } from '../crmActionRegistry'

import { useWealthboxEligibility } from './useWealthboxEligibility'

const LARGE_BATCH_THRESHOLD = 50

export const WealthboxTagsActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, results }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)

  const eligibility = useWealthboxEligibility(results)

  const [updateIntegrationEntities, { isLoading: saving }] = useUpdateCrossClientIntegrationEntitiesMutation()

  const { data, isFetching } = useGetConnectorEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.WEALTHBOX,
    entityType: 'tags',
    relatedTo: null,
  })

  const tags: TagOption[] = useMemo(() => {
    const payload = data?.data as Array<{ tags?: WealthboxTag[] }> | undefined
    const rawTags = payload?.flatMap((entry) => entry.tags ?? []) ?? []
    return rawTags.map((tag) => ({ id: String(tag.id), name: tag.name }))
  }, [data])

  const totalCount = results.length

  const noEligibleClients = eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const noEligibleAlert = (
    <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
      <i className='bi bi-info-circle' />
      <span>None of the selected clients are synced with Wealthbox. Add their Wealthbox ID in client profiles to proceed.</span>
    </Alert>
  )

  const bodyOverride =
    eligibility.status === 'loading' ? (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5'>
        <Spinner />
        <div className='paragraph-medium medium'>Checking Wealthbox eligibility…</div>
      </div>
    ) : noEligibleClients ? (
      noEligibleAlert
    ) : null

  const largeBatch = eligibility.status === 'ready' && eligibility.totalCount >= LARGE_BATCH_THRESHOLD
  const hasSkipped = eligibility.status === 'ready' && eligibility.skippedCount > 0
  const showBatchNotice = eligibility.status === 'ready' && eligibility.eligibleCount > 0 && (largeBatch || hasSkipped)

  const notice =
    eligibility.status === 'error' ? (
      <Alert variant='danger' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
        <i className='bi bi-exclamation-triangle' />
        <span>We couldn't check Wealthbox eligibility for the selected clients. Please close this dialog and try again.</span>
      </Alert>
    ) : showBatchNotice ? (
      <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
        <i className='bi bi-info-circle' />
        <span>
          {hasSkipped ? (
            <>
              You'll tag{' '}
              <strong>
                {eligibility.eligibleCount} of {eligibility.totalCount}
              </strong>{' '}
              clients in Wealthbox (<strong>{eligibility.skippedCount}</strong> not synced with Zocks).
            </>
          ) : (
            <>
              You are planning to tag <strong>{eligibility.totalCount}</strong> clients in Wealthbox.
            </>
          )}
          {largeBatch ? " This action can't be undone or stopped once started." : null}
        </span>
      </Alert>
    ) : null

  const primaryActionDisabled = eligibility.status !== 'ready' || eligibility.eligibleCount === 0

  const handleSave = async (selectedTags: TagOption[]) => {
    if (eligibility.status !== 'ready' || !execution?.id || !accountId) {
      return
    }

    try {
      await updateIntegrationEntities({
        accountId,
        executionId: execution.id,
        connector: IntegrationServiceId.WEALTHBOX,
        relatedToType: 'contact',
        sourcePlatform: 'zocks',
        sourceObject: 'tags',
        entityType: 'set_tags',
        entity: { tags: selectedTags.map((t) => ({ id: t.id, name: t.name })) },
        targets: eligibility.eligibleTargets,
      }).unwrap()

      dispatch(triggerCrossClientActionsPolling(Date.now()))
      dispatch(addToast({ variant: 'success', title: 'Assigning tags', messages: ['Wealthbox tag assignment started.'] }))
      onClose()
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Failed to assign tags', messages: ['Could not start the Wealthbox tag assignment. Please try again.'] }))
    }
  }

  return (
    <TagsModal
      show={open}
      onCancel={onClose}
      tags={tags}
      loading={isFetching}
      saving={saving}
      count={totalCount}
      onSave={handleSave}
      primaryActionTitle={noEligibleClients ? 'Done' : 'Assign tags'}
      notice={notice}
      primaryActionDisabled={noEligibleClients ? false : primaryActionDisabled}
      bodyOverride={bodyOverride}
      hideSecondaryAction={noEligibleClients}
      primaryActionOverride={noEligibleClients ? onClose : undefined}
    />
  )
}
